import THREEService from 'services/three-js-service';
import Junction from 'modules/junction';
import MaterialDatabase from 'services/material-database';

const modelUrlBase = 'static/models/';
const modelCache = {};

function Component(url, options) {
  this._url = url;
  this._getModelPromise = null;

  if (options) {
    this._id = options.id;
    this._name = options.name;
    this._type = options.type;
    this._junctions = options.junctions && options.junctions.map(
      (rawJunction) => {
        const junction = Junction.createFromJson(rawJunction);
        junction.parentComponent = this;
        return junction;
      });

    this._data = options;

    // Assign default materials
    options.parts.forEach((part) => {
      const materialData = MaterialDatabase.getMaterialData(part.material);
      this.setMaterial(part.id, materialData.material);
    });

    this._variations = {};
    this._variationValues = {};

    // Store the available variations and their values into dictionaries.
    if (options.variations) {
      options.variations.forEach((variation) => {
        this._variations[variation.id] = variation;
        this.setVariation(variation.id, variation.default);
      });
    }

    this._isCombination = this._data.tag &&
      this._data.tag.indexOf('combination') >= 0;
  }
}

Component.createFromJson = function createFromJson(json) {
  return new Component(`${modelUrlBase}${json.model}.obj`, json);
};

Component.createJunctionsDictByType =
  function createJunctionsDictByType(junctions) {
    return junctions.reduce((result, junction) => {
      let junctionsOfType = result[junction.type];
      if (!junctionsOfType) {
        result[junction.type] = junctionsOfType = [];
      }
      junctionsOfType.push(junction);
      return result;
    }, {});
  };

/**
 * Move all child components of the component to another one.
 *
 * @param {Component} srcComponent
 * @param {Component} descComponent
 * @returns {Promise.<Array>} dropped components in the movement
 */
Component.moveChildComponents =
  function moveChildComponents(srcComponent, destComponent) {
    if (!srcComponent || !destComponent) {
      return Promise.resolve();
    }

    const srcJunctions = srcComponent.getJunctionsOfType();
    const destJunctions = destComponent.getJunctionsOfType();

    const srcJunctionsByType =
      Component.createJunctionsDictByType(srcJunctions);
    const destJunctionsByType =
      Component.createJunctionsDictByType(destJunctions);

    const droppedComponents = [];

    return destComponent.getModel().then(() => {
      const promises = [];

      Object.keys(srcJunctionsByType).forEach((type) => {
        const srcJunctionsOfType = srcJunctionsByType[type];
        const destJunctionsOfType = destJunctionsByType[type];

        srcJunctionsOfType.forEach((srcJunction, i) => {
          const childComponent = srcJunction.childComponent;
          const destJunction = destJunctionsOfType && destJunctionsOfType[i];

          if (childComponent) {
            if (destJunction) {
              // remove the child component from the source junction and connect
              // it to the destination junction.
              promises.push(srcJunction.disconnect().then(() => {
                return destJunction.connect(childComponent);
              }));
            } else {
              droppedComponents.push({
                component: childComponent,
                index: i
              });
            }
          }
        });
      });

      return Promise.all(promises);
    }).then(() => {
      return droppedComponents;
    });
  };

Component.prototype = {
  get id() {
    return this._id;
  },
  get name() {
    return this._name;
  },
  get type() {
    return this._type;
  },
  get variations() {
    return this._variations;
  },
  get data() {
    return this._data;
  },
  get isCombination() {
    return this._isCombination;
  }
};

/**
 * Apply a variation to the component.
 *
 * @param {Object} variation A variation object
 * @param {String} value Specify the variation value.
 */
Component.prototype._applyVariation = function _applyVariation(variation,
  value) {
  const targetOption = variation.options.find(
    (option) => {
      return option.value === value;
    });

  if (!targetOption) {
    return;
  }

  targetOption.targets.forEach((targetObj) => {
    const targetElements = targetObj.target.split('.');
    if (targetElements[0] === 'parts') {
      if (targetElements[2] === 'material') {
        const materialData = MaterialDatabase.getMaterialData(targetObj.value);
        this.setMaterial(targetElements[1], materialData.material);
      }
    }
  });
};

/**
 * Get the model of the component.
 *
 * @returns {Promise.<THREE.Object3D>} The model of the component.
 */
Component.prototype.getModel = function getModel() {
  if (this._getModelPromise === null) {
    if (modelCache[this._url]) {
      this._getModelPromise = Promise.resolve(modelCache[this._url].clone());
    } else {
      this._getModelPromise = THREEService.loadObj(this._url).then((obj) => {
        modelCache[this._url] = obj;
        return obj.clone();
      });
    }
  }
  return this._getModelPromise;
};

/**
 * Get all junctions of a specified type. If the type is not specified, all
 * junctions are returned.
 *
 * @param {String} type
 * @param {Boolean} isRecursive
 * @returns {Array.<ApnItem>} All matched junctions.
 */
Component.prototype.getJunctionsOfType =
  function getJunctionsOfType(type, isRecursive) {
    if (this._junctions == null) {
      return [];
    }

    let result = [];

    this._junctions.forEach((junction) => {
      if (type == null || junction.type === type) {
        result.push(junction);
      }
    });

    if (isRecursive) {
      this._junctions.forEach((junction) => {
        if (junction.childComponent) {
          result = result.concat(
            junction.childComponent.getJunctionsOfType(type, true));
        }
      });
    }

    return result;
  };

Component.prototype.setMaterial = function setMaterial(partName, material) {
  return this.getModel().then((model) => {
    model.children.filter((child) => {
      return child.name === partName;
    }).forEach((child) => {
      child.material = material;
    });
  });
};

Component.prototype.setVariation =
  function setVariation(variationName, newValue) {
    const oldValue = this._variationValues[variationName];

    if (oldValue === newValue) {
      return;
    }

    this._variationValues[variationName] = newValue;
    this._applyVariation(this._variations[variationName], newValue);
  };

Component.prototype.getVariation = function getVariation(variationName) {
  return this._variationValues[variationName];
};

export default Component;



// WEBPACK FOOTER //
// ./src/modules/component.js